import { useEffect, useRef, useState } from "react";
import { getUserLoadRequests } from "apis/myRequestsService";
import { MyRequestParams, MyRequestResponse } from "./types";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";

const DEFAULT_PAGE_SIZE = 10;

// Owns the "Your Requests" data: fetching, pagination, sort, and status filter.
// Keeps the page component focused on presentation.
export function useLoadRequests(
  initialTargetRequestId?: number | string | null,
) {
  const { showLoader, hideLoader } = useGlobalLoader();

  const [flightsDetails, setFlightsDetails] = useState<MyRequestResponse>({
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Empty string = "Posted Date" (the default sort), sent as-is in the payload.
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const hasTargetResolvedRef = useRef(false);
  const prevTargetRequestIdRef = useRef(initialTargetRequestId);
  // Set right before setCurrentPage(foundPage) below, so the resulting
  // currentPage change doesn't trigger the effect's own duplicate refetch of
  // a page we already have data for. Without this, loading flips back to
  // true immediately after the target page renders, unmounting the row
  // before FlightLIsting's one-shot scroll-into-view timeout can find it.
  const skipNextFetchRef = useRef(false);

  // silent=true skips the global loader — used for the background refresh after
  // an optimistic delete so the page refills without a loader flash.
  const fetchLoadRequests = async (silent = false) => {
    if (!silent) {
      showLoader();
      setLoading(true);
    }
    setError(null);

    try {
      const params: MyRequestParams = {
        page: currentPage,
        size: pageSize,
        sortBy,
        sortOrder,
        status,
      };
      const data = await getUserLoadRequests(params);

      // If an initial target request ID was passed (from recently posted requests)
      // and has not been resolved yet, verify if it exists on page 1 or scan other pages.
      if (initialTargetRequestId && !hasTargetResolvedRef.current) {
        hasTargetResolvedRef.current = true;
        const targetIdNum = Number(initialTargetRequestId);
        const existsOnCurrentPage = data.items?.some(
          (item: any) => Number(item.id) === targetIdNum,
        );

        if (!existsOnCurrentPage && data.totalCount > pageSize) {
          const totalPages = Math.ceil(data.totalCount / pageSize);
          let foundPage: number | null = null;
          let foundData: MyRequestResponse | null = null;

          for (let p = 1; p <= totalPages; p++) {
            if (p === currentPage) continue;
            try {
              const otherPageData = await getUserLoadRequests({
                page: p,
                size: pageSize,
                sortBy,
                sortOrder,
                status,
              });
              if (
                otherPageData.items?.some(
                  (item: any) => Number(item.id) === targetIdNum,
                )
              ) {
                foundPage = p;
                foundData = otherPageData;
                break;
              }
            } catch (scanErr) {
              console.error(`Error searching page ${p} for request ${initialTargetRequestId}:`, scanErr);
            }
          }

          if (foundPage && foundData) {
            skipNextFetchRef.current = true;
            setCurrentPage(foundPage);
            setFlightsDetails(foundData);
            return;
          }
        }
      }

      // If the current page is now empty (e.g. the last item was deleted), step
      // back a page; the effect refetch will load it.
      if (data.items.length === 0 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
        return;
      }
      setFlightsDetails(data);
    } catch (err) {
      console.error("Error fetching load requests:", err);
      setError("Failed to fetch load requests");
    } finally {
      if (!silent) {
        setLoading(false);
        setTimeout(() => hideLoader(), 500);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // A new target ID (e.g. a second notification clicked while already on
    // this page) needs the resolve-and-highlight logic in fetchLoadRequests
    // to run again — otherwise hasTargetResolvedRef, set on the first target,
    // would silently skip every later one.
    if (initialTargetRequestId !== prevTargetRequestIdRef.current) {
      hasTargetResolvedRef.current = false;
      prevTargetRequestIdRef.current = initialTargetRequestId;
    }
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchLoadRequests();
  }, [status, currentPage, sortBy, sortOrder, pageSize, initialTargetRequestId]);

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(flightsDetails.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
    // Selection is intentionally NOT cleared on page change — selected
    // requests persist across pagination (Task 14).
  };

  // Change rows-per-page and jump back to the first page so the new page size
  // is applied from the top of the list.
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  return {
    flightsDetails,
    loading,
    error,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    status,
    setStatus,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    fetchLoadRequests,
    handlePageChange,
    handlePageSizeChange,
  };
}
